import React from 'react'
import { PieChart, Pie, Cell, Tooltip, PieLabelRenderProps, LabelList } from 'recharts'
import ChartTooltip from './ChartTooltip'
import { NumberType } from '@api'
import { PrettyNumber, prettyNumber } from '@ui'
import { sumBy } from 'lodash'
import isString from '@utils/isString'

const noData = [
  { label: '', value: 1, color: '#ddd' },
  { label: '', value: 1, color: '#ddd' },
  { label: '', value: 1, color: '#ddd' },
]

export interface IDonutData {
  label: string
  value: number
  color: string
  percent?: number
}

interface IProps {
  title: string
  width?: number
  height?: number
  onClick?: (a: any) => void
  showStats?: boolean
  data: IDonutData[]
  unit?: string
  showLabel?: boolean
  customTotalFormatter?: (total: number) => string
}

export default function DonutChart({
  data,
  title,
  unit,
  width = 200,
  height = 200,
  onClick,
  showStats = true,
  customTotalFormatter,
  showLabel,
}: IProps) {
  const skeleton = !data?.length || !data.reduce((acc, d) => acc + d.value, 0)
  const total = skeleton ? 0 : sumBy(data, 'value')
  const DATA: IDonutData[] = skeleton
    ? noData
    : data.map((d) => ({ ...d, percent: (d.value / total) * 100 }))

  const showStatsText = () => {
    let totalStr

    if (!customTotalFormatter) {
      totalStr = prettyNumber({
        value: total,
      })
    } else {
      totalStr = customTotalFormatter(total)
    }

    if (unit === '$') totalStr = `$${totalStr}`

    const fontSize1 = width < 160 && height < 160 ? 14 : 18
    const fontSize2 = width < 160 && height < 160 ? 12 : 14

    return (
      <g>
        <text
          fontSize={fontSize1}
          x={width / 2}
          y={height / 2}
          textAnchor="middle"
          fill="var(--black)"
        >
          {totalStr}
        </text>
        <text
          fontSize={fontSize2}
          x={width / 2}
          y={height / 2}
          dy={18}
          textAnchor="middle"
          fill="var(--grey1)"
        >
          Total
        </text>
      </g>
    )
  }

  const r = (0.6 * Math.min(width, height)) / 2
  const ir = 0.6 * r
  const or = r
  return (
    <PieChart width={width} height={height}>
      <Pie
        isAnimationActive={!skeleton}
        animationDuration={300}
        animationBegin={0}
        style={{ cursor: skeleton || !onClick ? 'default' : 'pointer' }}
        data={DATA}
        dataKey="value"
        nameKey="label"
        startAngle={90}
        endAngle={450}
        innerRadius={ir}
        outerRadius={or}
        label={
          skeleton
            ? () => null
            : !showLabel
            ? (props: PieLabelRenderProps) => <Label {...props} unit={unit} width={width} />
            : (props: PieLabelRenderProps) => (
                <RenderCustomizedLabel {...props} unit={unit} width={width} />
              )
        }
        onClick={(d) => onClick && onClick(d.payload)}
        labelLine={false}
      >
        {DATA.map((d: IDonutData, i: number) => (
          <Cell key={`chart-${title}-${i}`} fill={d.color} />
        ))}
      </Pie>

      {showStats && showStatsText()}
      {!skeleton && (
        <Tooltip
          animationDuration={0}
          content={(props: any) => <CustomTooltip unit={unit} {...props} />}
        />
      )}
    </PieChart>
  )
}

function CustomTooltip({ unit, active, payload }: any) {
  if (!active) return null

  const currencyEl = unit === '$' ? '$' : ''
  const unitEl = unit && unit !== '$' && (unit === '%' ? '%' : ` ${unit}`)

  return (
    <ChartTooltip
      label={payload[0].payload.label}
      value={
        <span>
          {currencyEl}
          <PrettyNumber value={payload[0].value} />
          {unitEl} (
          <PrettyNumber value={payload[0].payload.percent} numberType={NumberType.ROUNDED_NUMBER} />
          %)
        </span>
      }
    />
  )
}

const RenderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  unit,
}: PieLabelRenderProps) => {
  const RADIAN = Math.PI / 180
  const formattedValue = `${unit === '$' ? '$' : ''}${prettyNumber({
    value: percent || 0,
    numberType: NumberType.ROUNDED_NUMBER,
  })}%`
  if (
    innerRadius &&
    outerRadius &&
    cx &&
    cy &&
    midAngle &&
    !(typeof cx === 'string') &&
    !(typeof cy === 'string') &&
    !(typeof outerRadius === 'string') &&
    !(typeof innerRadius === 'string')
  ) {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        style={{ fontSize: '10px' }}
        x={x}
        y={y}
        fill="white"
        textAnchor="middle"
        dominantBaseline="central"
      >
        {formattedValue}
      </text>
    )
  } else {
    return <div />
  }
}

function Label({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  value,
  width,
  label,
  unit,
}: PieLabelRenderProps) {
  // force rerender on mount to recalulate label positions
  // eslint-disable-next-line
  let [mounted, setMounted] = React.useState(false)
  React.useEffect(() => {
    setMounted(true)
  }, [])

  const RADIAN = Math.PI / 180
  const nameBBox = React.useRef<SVGTextElement>(null)
  const valueBBox = React.useRef<SVGTextElement>(null)

  const name =
    isString(label) && label.length > 16 ? `${label.substring(0, 15)}...` : label || 'n/a'

  // Don't show label for small slices to avoid text overlap
  if (!percent || percent < 8) {
    return null
  }

  if (
    cx &&
    cy &&
    midAngle &&
    outerRadius &&
    innerRadius &&
    !(typeof cx === 'string') &&
    !(typeof cy === 'string') &&
    !(typeof outerRadius === 'string') &&
    !(typeof innerRadius === 'string') &&
    width
  ) {
    const sin = Math.sin(-RADIAN * midAngle)
    const cos = Math.cos(-RADIAN * midAngle)
    const sx = cx + (outerRadius - (outerRadius - innerRadius) / 2) * cos
    const sy = cy + (outerRadius - (outerRadius - innerRadius) / 2) * sin
    const mx = cx + (outerRadius + 5) * cos
    const my = cy + (outerRadius + 5) * sin
    const ex = mx + (cos >= 0 ? 1 : -1) * 22
    const ey = my

    let textAnchor1 = cos >= 0 ? 'start' : 'end'
    let textAnchor2 = textAnchor1

    const formattedValue = `${unit === '$' ? '$' : ''}${prettyNumber({
      value,
      numberType: NumberType.ROUNDED_NUMBER,
    })} (${prettyNumber({
      value: percent || 0,
      numberType: NumberType.ROUNDED_NUMBER,
    })}%)`
    let textX1 = ex + (cos >= 0 ? 1 : -1) * 12
    let textX2 = textX1

    if (
      nameBBox &&
      nameBBox.current &&
      nameBBox.current !== null &&
      valueBBox &&
      valueBBox.current &&
      valueBBox.current !== null
    ) {
      try {
        if (ex < width / 2) {
          textX1 = ex - nameBBox.current.getBBox().width < 0 ? 0 : ex
          textX2 = ex - valueBBox.current.getBBox().width < 0 ? 0 : ex
          textAnchor1 = ex - nameBBox.current.getBBox().width < 0 ? 'start' : 'end'
          textAnchor2 = ex - valueBBox.current.getBBox().width < 0 ? 'start' : 'end'
        } else {
          textX1 = ex + nameBBox.current.getBBox().width > width ? width : ex
          textX2 = ex + valueBBox.current.getBBox().width > width ? width : ex
          textAnchor1 = ex + nameBBox.current.getBBox().width > width ? 'end' : 'start'
          textAnchor2 = ex + valueBBox.current.getBBox().width > width ? 'end' : 'start'
        }
      } catch (err) {}
    }

    return (
      <g fontSize={12}>
        <path d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`} stroke="var(--border-color)" fill="none" />
        <circle cx={ex} cy={ey} r={2} fill="var(--border-color)" stroke="none" />
        <text
          ref={nameBBox}
          x={textX1}
          y={ey - 5}
          textAnchor={textAnchor1}
          fill="var(--grey1)"
          style={{ textShadow: '1px 1px 3px white' }}
        >{`${name}`}</text>
        <text
          ref={valueBBox}
          x={textX2}
          y={ey - 3}
          dy={14}
          textAnchor={textAnchor2}
          fill="#555"
          style={{
            fontSize: 12,
            fontWeight: 700,
            textShadow: '1px 1px 3px white',
          }}
        >
          {formattedValue}
        </text>
      </g>
    )
  } else {
    return <div />
  }
}
